import { useEffect, useRef, useState } from "react";

// Sits above the palette. Shows the current canvas name as a button that
// opens a plump dropdown listing all saved canvases plus actions for
// New / Rename / Delete. The visual language matches the rest of the
// app: rounded-2xl, ink borders, plump shadow, paper background.

export default function TopBar({
  list,
  current,
  loading,
  onSwitch,
  onCreate,
  onRename,
  onDelete,
}) {
  const [open, setOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renaming, setRenaming] = useState(false);
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const renameInputRef = useRef(null);

  useEffect(() => {
    if (!renaming) return;
    const el = renameInputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [renaming]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setOpen(false);
      setRenaming(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); setRenaming(false); }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleNew = async () => {
    setOpen(false);
    await onCreate({ name: defaultNewName(list) });
  };

  const handleRenameStart = () => {
    setRenameValue(current?.name || "");
    setRenaming(true);
  };
  const handleRenameSubmit = async () => {
    if (!current) { setRenaming(false); return; }
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== current.name) {
      await onRename(current.id, trimmed);
    }
    setRenaming(false);
  };
  const handleDelete = async () => {
    if (!current) return;
    const sure = confirm(`Delete "${current.name}"?`);
    if (!sure) return;
    setOpen(false);
    await onDelete(current.id);
  };

  return (
    <header className="relative flex h-12 items-center gap-3 border-b-2 border-paper-edge bg-paper px-5">
      <span className="text-sm font-bold uppercase tracking-wider text-ink-soft">Canvas</span>

      {renaming ? (
        <form
          onSubmit={(e) => { e.preventDefault(); handleRenameSubmit(); }}
          className="flex items-center gap-2"
        >
          <input
            ref={renameInputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={handleRenameSubmit}
            className="rounded-xl border-2 border-ink bg-white px-3 py-1.5 text-sm font-semibold text-ink outline-none"
            size={Math.max(8, renameValue.length + 2)}
          />
        </form>
      ) : (
        <button
          ref={triggerRef}
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 rounded-2xl border-2 border-ink/20 bg-white px-3 py-1.5 text-sm font-semibold text-ink transition hover:border-ink hover:bg-paper-deep"
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <span className="max-w-[20rem] truncate">
            {loading ? "…" : current?.name || "Untitled"}
          </span>
          <span aria-hidden className="text-xs text-ink-soft">▾</span>
        </button>
      )}

      <button
        onClick={handleNew}
        title="Create a new blank canvas"
        className="flex items-center gap-1.5 rounded-2xl border-2 border-ink bg-cyan-soft px-3 py-1.5 text-sm font-semibold text-ink shadow-plump transition hover:bg-cyan-bright hover:-translate-y-0.5"
      >
        <span className="text-base leading-none">＋</span>
        New canvas
      </button>

      {current && !renaming && (
        <>
          <button
            onClick={handleRenameStart}
            className="rounded-2xl border-2 border-ink/20 bg-white px-3 py-1.5 text-sm font-semibold text-ink transition hover:border-ink hover:bg-paper-deep"
          >
            Rename
          </button>
          <button
            onClick={handleDelete}
            className="rounded-2xl border-2 border-ink/20 bg-white px-3 py-1.5 text-sm font-semibold text-ink transition hover:border-coral hover:bg-coral-soft"
          >
            Delete
          </button>
        </>
      )}

      {open && (
        <div
          ref={popRef}
          role="menu"
          className="absolute left-24 top-[calc(100%+6px)] z-40 w-72 rounded-2xl border-2 border-ink bg-paper py-2 shadow-plump"
        >
          <div className="border-b border-paper-edge px-3 pb-2 text-[11px] uppercase tracking-wider text-ink-soft">
            Saved canvases ({list.length})
          </div>
          <ul className="max-h-72 overflow-y-auto py-1">
            {list.length === 0 && (
              <li className="px-3 py-2 text-xs text-ink-soft">No canvases yet.</li>
            )}
            {list.map((c) => {
              const active = c.id === current?.id;
              return (
                <li key={c.id}>
                  <button
                    onClick={() => { setOpen(false); onSwitch(c.id); }}
                    className={
                      "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition " +
                      (active
                        ? "bg-cyan-soft font-semibold text-ink"
                        : "text-ink hover:bg-paper-deep")
                    }
                  >
                    <span className="w-4 text-base leading-none text-ink">{active ? "●" : "○"}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="text-[10px] tabular-nums text-ink-faint">
                      {timeAgo(c.lastOpenedAt)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-paper-edge px-3 pt-2">
            <button
              onClick={handleNew}
              className="flex w-full items-center justify-center gap-1.5 rounded-xl border-2 border-ink bg-cyan-soft px-3 py-1.5 text-sm font-semibold text-ink shadow-plump transition hover:bg-cyan-bright"
            >
              <span className="text-base leading-none">＋</span>
              New blank canvas
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

function defaultNewName(list) {
  const base = "Untitled";
  const used = new Set(list.map((c) => c.name));
  if (!used.has(base)) return base;
  for (let i = 2; i < 9999; i++) {
    const candidate = `${base} ${i}`;
    if (!used.has(candidate)) return candidate;
  }
  return base;
}

function timeAgo(ms) {
  if (!ms) return "";
  const diff = Date.now() - ms;
  const s = Math.round(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}
